// Equipment catalog and recipe packages for the in-game shop
// Each item has a clear mechanical effect wired into the simulation.

// ── Equipment Catalog ─────────────────────────────────────────────────────────
// One-time purchase per room. Installed permanently; effects active every tick.
export const EQUIPMENT_CATALOG = [
  {
    id: 'lights-basic',
    name: 'Basic LED Array',
    icon: '💡',
    description: 'Entry-level 200W LED lights. Enables artificial lighting control. Required for greenhouses — unlocks full THC accumulation rate.',
    cost: 800,
    category: 'lighting',
    targetEquipment: 'lights',
    effect: { installed: true, efficiency: 0.65 },
    // Mechanical impact (explained in UI):
    // Without lights: greenhouse runs at 65% light level → THC builds at 65% rate.
    // With lights ON: light level rises to ~0.90 (65% base + 0.65 efficiency × 30%).
    // Net result: ~38% more THC accumulated by harvest → meaningfully higher quality scores.
  },
  {
    id: 'lights-premium',
    name: 'Full-Spectrum LED Bar',
    icon: '⚡',
    description: 'High-output full-spectrum LED strip. Efficiency 0.90 → 100% light level AND a +10% THC accumulation bonus from spectral quality.',
    cost: 2200,
    category: 'lighting',
    targetEquipment: 'lights',
    effect: { installed: true, efficiency: 0.90 },
    // Mechanical impact:
    // efficiency >= 0.85 triggers the thcLightBonus (×1.10) in environmental.js.
    // Combined with 100% light level: ~52% more THC than no-lights greenhouse.
  },
  {
    id: 'co2-system',
    name: 'CO₂ Injection System',
    icon: '🟢',
    description: 'Automated CO₂ controller with regulator + diffusers. Enables CO₂ enrichment targeting. Requires CO₂ tech (A2) to be researched.',
    cost: 1200,
    category: 'co2',
    targetEquipment: 'co2sys',
    effect: { installed: true },
    // Mechanical impact:
    // environmental.js checks room.equipment.co2sys?.installed before allowing CO₂ drift
    // toward the recipe target. At 1200ppm CO₂, stress from CO₂ deficiency drops to 0.
    // Also opens up the CO₂ Enrichment recipe package.
  },
  {
    id: 'hvac-upgrade',
    name: 'HVAC Efficiency Upgrade',
    icon: '⚙️',
    description: 'Commercial-grade HVAC components. Raises efficiency from 0.70 → 0.90, cutting temp/humidity drift by ~25%. Less drift = less VPD stress.',
    cost: 1500,
    category: 'climate',
    targetEquipment: 'hvac',
    effect: { efficiency: 0.90, health: 100 },
    // Mechanical impact:
    // computeDrift uses hvacEff × 0.12 for correction force and (1 - hvacEff) for noise.
    // 0.70 → 0.90 means: correction force +28%, noise magnitude cut by -67%.
    // Tighter temp/humidity = fewer VPD deviations = less stressDelta every tick.
  },
  {
    id: 'smart-irrigation',
    name: 'Smart Drip Irrigation',
    icon: '💧',
    description: 'Automated nutrient delivery (automationTier 0 → 1). Cuts humidity-driven stress component by 30% every tick through precise root-zone watering.',
    cost: 900,
    category: 'irrigation',
    targetEquipment: 'irrigation',
    effect: { automationTier: 1, health: 100 },
    // Mechanical impact:
    // irrigationFactor = irrigationTier >= 1 ? 0.70 : 1.0 — applied to effectiveStressDelta.
    // Reduces all accumulated stress by 30%, which compounds over a full grow cycle.
    // On a 60-day veg+flower run: stress at harvest ~20–30% lower with smart irrigation.
  },
];

// ── Recipe Packages ───────────────────────────────────────────────────────────
// Purchased per room. Modifies room.recipe fields — active until replaced.
// Multiple packages can overwrite each other; last applied wins.
export const RECIPE_PACKAGES = [
  {
    id: 'recipe-super-bloom',
    name: 'Super Bloom Formula',
    icon: '🌸',
    description: 'P/K-rich bloom booster. +40% defoliation effectiveness AND +0.02 terpene per tick during flowering for richer flavour profiles.',
    cost: 400,
    category: 'nutrients',
    recipeChanges: {
      defoliateMultiplier: 1.4,
      bloomBoost: true,
      stressShield: false,
      yieldMaximizer: false,
    },
    // Mechanical impact:
    // defoliateMultiplier 1.4×: each defoliate reduces ~20 stress instead of ~14 (at 50% effectiveness).
    // bloomBoost: +0.02/tick terpene during flower = +0.02 × 56×24 = +26.9 terpene points over a full flower stage.
    // At 35% quality weight: +0.35 × 26.9 ≈ +9 quality score points.
  },
  {
    id: 'recipe-stress-shield',
    name: 'Stress Shield Protocol',
    icon: '🛡️',
    description: 'Silicon-enriched foliar spray. Reduces ALL environmental stress by 20% (stressShieldFactor) and +20% defoliate effectiveness.',
    cost: 300,
    category: 'nutrients',
    recipeChanges: {
      defoliateMultiplier: 1.2,
      stressShield: true,
      bloomBoost: false,
      yieldMaximizer: false,
    },
    // Mechanical impact:
    // stressShieldFactor = 0.80 applied to effectiveStressDelta every tick.
    // Means: health degrades 20% slower, yield potential holds 20% better.
    // Over a 60-day cycle: +5–8% health at harvest → meaningfully more grams.
  },
  {
    id: 'recipe-yield-max',
    name: 'Yield Maximizer Stack',
    icon: '📈',
    description: 'Aggressive veg nutrition. +0.008 yield potential per tick during vegetative stage, recovering potential lost to early stress.',
    cost: 550,
    category: 'nutrients',
    recipeChanges: {
      defoliateMultiplier: 1.1,
      yieldMaximizer: true,
      bloomBoost: false,
      stressShield: false,
    },
    // Mechanical impact:
    // yieldMaximizer adds +0.008 yield potential per tick during 'vegetative' stage.
    // Over a 21-day veg stage (504 ticks): +0.008 × 504 = +4.0 yield potential points.
    // yield potential feeds directly into finalGrams formula: can add 5–10g per harvest.
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
export function getEquipmentItem(id) {
  return EQUIPMENT_CATALOG.find(e => e.id === id) || null;
}

export function getRecipePackage(id) {
  return RECIPE_PACKAGES.find(r => r.id === id) || null;
}
